// wayai eval — dispatcher for imperative eval actions (authoring is file-driven via wayai push/pull).
export async function evalCommand(args: string[]): Promise<void> {
  const [sub, ...rest] = args;

  if (!sub) {
    printHelp();
    process.exit(1);
  }

  switch (sub) {
    case 'capture': {
      const { evalCaptureCommand } = await import('./eval-capture.js');
      await evalCaptureCommand(rest);
      return;
    }
    case 'journey': {
      const [journeySub, ...journeyRest] = rest;
      if (journeySub === 'capture') {
        const { journeyCaptureCommand } = await import('./journey-capture.js');
        await journeyCaptureCommand(journeyRest);
        return;
      }
      console.error(`Unknown eval journey subcommand: ${journeySub ?? '(none)'}`);
      printHelp();
      process.exit(1);
      return;
    }
    case 'session': {
      const [sessionSub, ...sessionRest] = rest;
      if (sessionSub === 'delete') {
        const { evalSessionDeleteCommand } = await import('./eval-session-delete.js');
        await evalSessionDeleteCommand(sessionRest);
        return;
      }
      console.error(`Unknown eval session subcommand: ${sessionSub ?? '(none)'}`);
      printHelp();
      process.exit(1);
      return;
    }
    case 'help':
    case '--help':
    case '-h':
      printHelp();
      return;
    default:
      console.error(`Unknown eval subcommand: ${sub}`);
      printHelp();
      process.exit(1);
  }
}

function printHelp(): void {
  console.log(`
wayai eval — eval scenario operations

Usage:
  wayai eval <subcommand> [options]

Subcommands:
  capture <conversation_id>          Capture a real conversation's last
                                     exchange as a scenario YAML in evals/.
  journey capture <conversation_id>  Capture a real conversation's FULL
                                     transcript as a platform-managed journey.
  session delete <session_id>        Delete one eval session (and its
                                     conversations). Destructive, irreversible.
  session delete --all               Delete every eval session on the hub.

Capture flags (scenario):
  --set <name>            Scenario set folder (created if missing). Default: "Default".
  --name <eval_name>      Scenario name. Default: derived from conversation ID.
  --instructions <text>   Evaluator instructions for the scoring agent.

Journey capture flags:
  --name <journey_name>   Journey name. Default: derived from conversation ID.
  --instructions <text>   Default evaluator instructions for the journey's steps.

Session delete flags:
  --all                   Delete ALL sessions on the hub instead of one.
  -y, --yes               Skip the confirmation prompt.

Examples:
  wayai eval capture 11111111-2222-3333-4444-555555555555
  wayai eval capture <conv_id> --set regression-suite --name "Cancel order"
  wayai eval journey capture <conv_id> --name "Refund happy path"
  wayai eval session delete 11111111-2222-3333-4444-555555555555
  wayai eval session delete --all -y

Authoring evals (create/update/delete) is file-driven — edit YAML in
evals/ and run \`wayai push\`. Capture commands are for production replay only.
A journey owns its scenario set + steps, so it is platform-managed (no local
file) — run \`wayai pull\` to bring it down if you want it in the workspace.
`.trim());
}
